/** @format */

import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import { WINDOW } from './window/window.service';
import { Logger } from './logger.service';
import { LCP_EVENT_NAME, LCP_STABILITY_TIMEOUT_MS } from '@shared/data-access/constants';
import { PERFORMANCE_CONFIG } from '@shared/data-access/config';

@Injectable({
  providedIn: 'root',
})
export class PerformanceService implements OnDestroy {
  private readonly config = inject(PERFORMANCE_CONFIG, { optional: true });
  private readonly logger = inject(Logger);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);

  readonly lcpValue = signal<PerformanceEntry | null>(null);

  private performanceObserver: PerformanceObserver | null = null;
  private timeoutId: NodeJS.Timeout | null = null;
  private stopTrigger: (() => void) | null = null;

  constructor() {
    if (this.config?.autoInitialize) {
      this.initializeLCPObserver();
    }
  }

  /**
   * Initializes the PerformanceObserver to track Largest Contentful Paint (LCP).
   * Sets up event listeners to stop tracking on user interaction.
   */
  private initializeLCPObserver(): void {
    // Only initialize in the browser and if not already initialized
    if (!isPlatformBrowser(this.platformId) || this.performanceObserver) {
      return;
    }

    // Skip LCP tracking if the page is loaded in the background
    if (this.window.document.visibilityState === 'hidden') {
      this.logger.log('LCP tracking skipped: Page loaded in background.');
      return;
    }

    try {
      // Create a PerformanceObserver to monitor LCP entries
      this.performanceObserver = new PerformanceObserver((entryList) => {
        const entries = entryList.getEntries();
        const lastEntry = entries.at(-1);

        // Update the signal with the latest LCP entry
        if (lastEntry) {
          this.lcpValue.set(lastEntry);
          // Reset the stability timer on each new entry
          this.resetStabilityTimerAndDispatchLCP();
        }
      });

      // Start observing LCP entries
      this.performanceObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      // Stop observing LCP on user interaction (to improve accuracy)
      this.stopTrigger = () => this.finalizeLCP();
      ['pointerdown', 'keydown', 'scroll'].forEach((event) =>
        this.window.addEventListener(event, this.stopTrigger, { once: true, passive: true }),
      );
    } catch (e) {
      this.logger.error('PerformanceObserver not supported.', e);
    }
  }

  /**
   * Resets the stability timer each time a new LCP entry is observed.
   * If no new entries are observed within the timeout, the LCP event is dispatched.
   */
  private resetStabilityTimerAndDispatchLCP(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      // Dispatch the LCP event if configured to do so immediately
      if (this.config?.dispatchLCPEventImmediately) {
        this.dispatchLCPEvent();
      }
      this.finalizeLCP();
    }, LCP_STABILITY_TIMEOUT_MS);
  }

  /**
   * Cleans up the PerformanceObserver and any pending timeouts.
   */
  private finalizeLCP(): void {
    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Explicitly remove event listeners to prevent memory leaks
    if (this.stopTrigger) {
      ['pointerdown', 'keydown', 'scroll'].forEach((event) =>
        this.window.removeEventListener(event, this.stopTrigger, {
          once: true,
          passive: true,
        } as EventListenerOptions),
      );

      this.stopTrigger = null;
    }
  }

  /**
   * Dispatches the LCP event with the current LCP entry.
   * If no entry is available, it attempts to retrieve it manually.
   */
  dispatchLCPEvent(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Prevent duplicate dispatch if the window flag is already set
    if (this.window[LCP_EVENT_NAME]) {
      this.logger.log('LCP Event already dispatched previously.');
      return;
    }

    // Ensure we have an LCP entry, try to retrieve it if not set
    const entry = this.lcpValue() ?? this.retrieveLCPEntryManually() ?? null;
    this.lcpValue.set(entry);

    // Create and dispatch the LCP event (even if entry is null)
    this.createAndDispatch(entry);
  }

  /**
   * Creates and dispatches the LCP CustomEvent.
   * @param entry The PerformanceEntry representing the LCP, or null if no LCP was found.
   */
  private createAndDispatch(entry: PerformanceEntry | null): void {
    const lcpEvent = new CustomEvent(LCP_EVENT_NAME, {
      bubbles: true,
      detail: entry,
    });

    this.window.dispatchEvent(lcpEvent);
    // Flag that the LCP event has been dispatched for late listeners
    this.window[LCP_EVENT_NAME] = true;
    this.logger.log('LCP Event dispatched:', entry);
  }

  /**
   * Ensures that the LCP entry is retrieved manually if not already set.
   */
  private retrieveLCPEntryManually(): PerformanceEntry | null {
    if (
      !this.window?.performance ||
      typeof this.window.performance.getEntriesByType !== 'function'
    ) {
      return null;
    }

    return this.window.performance.getEntriesByType('largest-contentful-paint')?.at(-1) ?? null;
  }

  ngOnDestroy(): void {
    this.finalizeLCP();
  }
}
